import React, { useState, useEffect, useCallback } from "react";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import DonutChart1 from "../components/Dashboardviz/D02/DB02Viz2";
import DonutChart2 from "../components/Dashboardviz/D02/DB02Viz3";
import DonutChart3 from "../components/Dashboardviz/D02/DB02Viz4";
import DonutChart4 from "../components/Dashboardviz/D02/DB02Viz5";
import PieChart1 from "../components/Dashboardviz/D02/DB02Viz1";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import TextBoxComponent from "../components/Dashboardviz/D02/DB02Viz6";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";
import "./D02_Overall_Engagement.css";

const D02_Overall_Engagement = ({ title }) => {
  const { filterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [isNarrativeOpen, setIsNarrativeOpen] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Toggle navigation sidebar
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // Toggle filter sidebar
  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen((prev) => !prev);
  }, []);

  // Toggle narrative popup
  const toggleNarrative = useCallback(() => {
    setIsNarrativeOpen((prev) => !prev);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Ensure charts resize properly when window size changes
  useEffect(() => {
    const resizeCharts = () => {
      // If you're using ApexCharts
      if (window.ApexCharts) {
        const charts = document.querySelectorAll('.apexcharts-canvas');
        charts.forEach(chart => {
          try {
            chart.style.width = '100%';
            const chartInstance = window.ApexCharts.getChartByID(
              chart.closest('.apexchart-container')?.id
            );
            if (chartInstance) {
              chartInstance.updateOptions({
                chart: {
                  width: '100%'
                }
              });
            }
          } catch (e) {
            console.error("Chart resize error:", e);
          }
        });
      }
    };
    
    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(resizeCharts, 100);
    
    return () => clearTimeout(timer);
  }, [windowSize]);

  // Utility functions
  const calculatePercentage = (group, total) =>
    total > 0 ? ((group / total) * 100).toFixed(1) : 0;

  const groupByField = (data, field) => {
    return data.reduce((acc, item) => {
      const key = item[field] || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  };

  const getTopGroup = (group) => {
    const entries = Object.entries(group || {});
    if (entries.length === 0) {
      return { name: "No Data Available", count: 0 };
    }
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return { name: sorted[0][0], count: sorted[0][1] };
  };

  const calculateFavorablePercentageByCategory = (
    filteredData,
    categoryKey
  ) => {
    const groupedByCategory = filteredData.reduce((acc, item) => {
      const categoryValue = item[categoryKey] || "Unknown";
      if (!acc[categoryValue]) acc[categoryValue] = { total: 0, favorable: 0 };
      acc[categoryValue].total += 1;

      const { MQ0026, MQ0095, MQ0302, MQ0321 } = item;
      if (
        (MQ0026 === 4 || MQ0026 === 5) &&
        (MQ0095 === 4 || MQ0095 === 5) &&
        (MQ0302 === 4 || MQ0302 === 5) &&
        (MQ0321 === 4 || MQ0321 === 5)
      ) {
        acc[categoryValue].favorable += 1;
      }

      return acc;
    }, {});

    const percentages = Object.entries(groupedByCategory).map(
      ([key, value]) => ({
        name: key,
        percentage: calculatePercentage(value.favorable, value.total),
      })
    );

    return percentages.reduce(
      (top, current) =>
        parseFloat(current.percentage) > parseFloat(top.percentage)
          ? current
          : top,
      { name: "No Data", percentage: 0 }
    );
  };

  const generateNarrative = () => {
    const filteredData =
      filterCriteria && Object.keys(filterCriteria).length > 0
        ? jsonData.filter((item) =>
            Object.entries(filterCriteria).every(([key, value]) =>
              value.length ? value.includes(item[key]) : true
            )
          )
        : jsonData;

    if (filteredData.length === 0) {
      return "No data available for the selected filters.";
    }

    const totalResponses = filteredData.length;
    const locations = groupByField(filteredData, "demographic_category_01");
    const generations = groupByField(filteredData, "demographic_category_05");
    const tenures = groupByField(filteredData, "demographic_category_04");

    const topLoc = getTopGroup(locations);
    const topGen = getTopGroup(generations);
    const topTen = getTopGroup(tenures);

    const topLocPercentage = calculatePercentage(topLoc.count, totalResponses);
    const topGenPercentage = calculatePercentage(topGen.count, totalResponses);
    const topTenPercentage = calculatePercentage(topTen.count, totalResponses);

    return `
      Overall Level of Engagement
      Locations:
      Respondents from ${topLoc.name} indicated the highest Overall Level of Engagement with ${topLocPercentage}% of those from this location reporting Favorable responses across the Engagement question set.

      Generations:
      Respondents in the ${topGen.name} group indicated the highest Overall Level of Engagement with ${topGenPercentage}% reporting Favorable responses across the Engagement question set.

      Tenure Groups:
      Respondents in the group with ${topTen.name} indicated the highest Overall Level of Engagement with ${topTenPercentage}% reporting Favorable responses across the Engagement question set.
    `;
  };

  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        // Apply sorting based on sortOrder if available
        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues = uniqueValues.sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });

      return options;
    };

    setFilterOptions(generateFilterOptions());
  }, []);

  return (
    <div className="D02_Overall_Engagement-container">
      <Header
        title={title}
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
      />
      <NavigationSidebar
        isOpen={isSidebarOpen}
        className={
          isSidebarOpen
            ? "D02_Overall_Engagement-sidebar-open"
            : "D02_Overall_Engagement-sidebar-closed"
        }
      />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterOptions}
      />

      <div className="D02_Overall_Engagement-narrative-button-container">
        <button
          className="D02_Overall_Engagement-narrative-button"
          onClick={toggleNarrative}
        >
          Narrative Insights
        </button>
      </div>

      <div className="D02_Overall_Engagement-main-content">
        <div className="D02_Overall_Engagement-content">
          <div className="D02_Overall_Engagement-content-row">
            <div className="D02_Overall_Engagement-card D02_Overall_Engagement-pie-chart-container">
              <div className="D02_Overall_Engagement-card-header">
                Level of Engagement
              </div>
              <div className="D02_Overall_Engagement-card-content">
                <div className="D02_Overall_Engagement-pie-chart">
                  <PieChart1 filterCriteria={filterCriteria} />
                </div>
              </div>
            </div>
            <div className="D02_Overall_Engagement-card D02_Overall_Engagement-text-box-container">
              <TextBoxComponent />
            </div>
          </div>

          <div className="D02_Overall_Engagement-chart-container">
            {[
              "Sense of Accomplishment",
              "Contribution",
              "Recommendation",
              "Input and Feedback",
            ].map((chartName, index) => (
              <div
                key={index}
                className="D02_Overall_Engagement-card D02_Overall_Engagement-donut-chart"
              >
                <div className="D02_Overall_Engagement-card-header">
                  {chartName}
                </div>
                <div className="D02_Overall_Engagement-card-content">
                  {[DonutChart1, DonutChart2, DonutChart3, DonutChart4][index]({
                    filterCriteria,
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isNarrativeOpen && (
        <div className="D02_Overall_Engagement-narrative-modal">
          <div className="D02_Overall_Engagement-narrative-content">
            <button
              className="D02_Overall_Engagement-narrative-close-button"
              onClick={toggleNarrative}
            >
              Close
            </button>
            <h3>Summary</h3>
            <p>{generateNarrative()}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default D02_Overall_Engagement;